import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { useFetchCaptionText, useUpdateCaptionText } from '@src/hooks/react-query/useCaptionsData';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';

export const VpCaptionEditor = ({ open, onClose, captionId, mediaId }) => {
  const [editedText, setEditedText] = useState('');
  const { data: textResponse, isLoading } = useFetchCaptionText(open ? captionId : null);
  const { mutate: updateText, isLoading: isSaving } = useUpdateCaptionText(mediaId);
  const { addSnackbarError, addSnackbarSuccess, addSnackbarInfo } = useVpSnackbar();

  useEffect(() => {
    if (textResponse?.text != null) {
      setEditedText(textResponse.text);
    }
  }, [textResponse]);

  const handleSave = () => {
    if (!editedText.trim()) {
      addSnackbarInfo({
        message: 'Caption text cannot be empty',
        severity: 'error',
        title: 'Error'
      });
      return;
    }
    updateText(
      { captionId, text: editedText.trim() },
      {
        onSuccess: () => {
          addSnackbarSuccess({ message: 'Caption text updated.' });
          onClose();
        },
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          Edit Captions
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <Stack gap="12px" sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Edit the text below to fix any transcription errors. Timestamps will be adjusted automatically.
            </Typography>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              variant="outlined"
              placeholder="Caption text..."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={isSaving || isLoading || !editedText.trim()}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VpCaptionEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  captionId: PropTypes.string,
  mediaId: PropTypes.string
};

export default VpCaptionEditor;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpCaptionEditor/VpCaptionEditor.jsx